import classNames from "classnames";
import { AppKind } from "common/components/app-kind";
import { getLocalizedString } from "common/components/localization";
import type { WireChoiceComponent } from "common/wire/base-components";
import * as React from "react";
import { useLayer } from "react-laag";
import { GlideIcon } from "sharedUI/ui/views/glide-icon/glide-icon";
import tw from "twin.macro";
import { APP_MODAL_ROOT } from "webapp/lib/constants";
import { css } from "webapp/styled-components";
import type { WireRenderer } from "webapp/wire-renderers/wire-renderer";

export const WireChoice: WireRenderer<WireChoiceComponent> = React.memo(props => {
    const { componentTitle, items, selectedValue, backend, isRequired } = props;
    // const currentIndex = items.findIndex(i => i.value === selectedValue.value);
    // Use selectedValue.onChangeToken to change the value.  If it's
    // `undefined` then the component should be disabled.

    const [isOpen, setIsOpen] = React.useState(false);

    const { renderLayer, layerProps, triggerProps, triggerBounds } = useLayer({
        isOpen,
        possiblePlacements: ["bottom-center", "top-center"],
        placement: "bottom-center",
        auto: true,
        onOutsideClick: () => setIsOpen(false),
        triggerOffset: 4,
        container: APP_MODAL_ROOT,
    });

    const selectedDisplayValue = items.find(i => i.value === selectedValue.value)?.displayAs;

    return (
        <div tw="flex flex-col">
            <div tw="mb-2 text-sm font-semibold flex justify-between items-baseline">
                <h3 tw="flex-shrink overflow-hidden text-text-contextual-dark overflow-ellipsis whitespace-nowrap">{componentTitle}</h3>
                {isRequired && (
                    <div tw="flex-shrink-0 ml-2 text-xs text-text-xpale font-normal">
                        {getLocalizedString("required", AppKind.Page)}
                    </div>
                )}
            </div>
            <button
                {...triggerProps}
                data-testid="wc-trigger"
                disabled={selectedValue.onChangeToken === undefined}
                tw="shadow-sm px-3 flex items-center justify-between rounded-lg bg-bg-front h-10 transition
                    hover:(shadow-sm-dark bg-bg-middle) focus:(ring-accent ring-2) disabled:(pointer-events-none opacity-40)"
                onClick={e => {
                    e.stopPropagation();
                    setIsOpen(cv => !cv);
                }}>
                <div
                    className={classNames(selectedDisplayValue !== undefined && "has-selection")}
                    css={css`
                        &.has-selection {
                            ${tw`text-text-dark`}
                        }
                    `}
                    tw="flex-1 text-left text-text-pale">
                    {selectedDisplayValue ?? "Choose something"}
                </div>
                <GlideIcon tw="flex-shrink-0" kind="stroke" icon="st-caret" iconSize={20} />
            </button>
            {isOpen &&
                renderLayer(
                    <div
                        {...layerProps}
                        style={{ width: triggerBounds?.width, ...layerProps.style }}
                        tw="bg-bg-front text-text-dark rounded-lg shadow-lg-dark overflow-hidden">
                        <ul tw="flex flex-col max-height[calc(50vh - 40px)] overflow-y-auto my-2">
                            {items.map((i, index) => (
                                <li
                                    key={index}
                                    data-testid="wc-item"
                                    className={classNames(i.value === selectedValue.value && "selected")}
                                    css={css`
                                        &.selected {
                                            ${tw`bg-bg-back font-medium`}
                                        }
                                    `}
                                    tw="text-sm font-normal px-3 py-1.5 hocus:bg-bg-back cursor-pointer not-last:mb-px"
                                    onClick={e => {
                                        e.stopPropagation();
                                        setIsOpen(false);
                                        if (selectedValue.onChangeToken !== undefined) {
                                            backend.valueChanged(selectedValue.onChangeToken, i.value);
                                        }
                                    }}>
                                    {i.displayAs}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
        </div>
    );
});
